import { Injectable } from '@angular/core';

import { of, map, filter, throwError, catchError, Observable } from 'rxjs';

import {
  DataForNewPartyRequest,
  IWaitingPartiesResponse,
  PartiesByWorkTimeRequest,
  Party,
  PartyRequest,
} from '@shared/party/models';
import {
  IParty,
  IParams,
  IPageData,
  ApiResponseModelWrapper,
  IDataNewPartyApiResponse,
} from '@tablesready/common-ui';
import { ApiClientService } from '@tablesready/api-client';

@Injectable({providedIn: 'root'})
export class PartiesService {
  // TODO(IParty -> Party): vestigial local cache. It predates the Akita store - the app now
  // sources party state from the store + the WS partyUpdated stream, and nothing reads
  // `parties` anymore. updateParty()/upsert() and their 5 callers (chat-flow x4 +
  // savePartiesBulk) still write to this unread array as a leftover so the writes are effectively no-ops.
  // Follow up: review the dependent callers that still route through this cache, confirm nothing relies on the optimistic path at runtime
  // (touches guest SMS/voice paging), then delete the triad + caller lines and retype to Party.
  public parties: Array<IParty> = [];
  public waitlistIdLength: number = 8;
  private readonly basePath = '/v.1/parties';

  constructor(private readonly http: ApiClientService) { }

  //TODO: check this
  saveParty(party: PartyRequest & { isHidden?: boolean }): Observable<Party> {
    party.waitlistId = party.waitlistId || this.makeId();
    // Callers often pass a copy of a cached party; isHidden is list display state, not party data
    const { isHidden, ...body } = party;

    return this.http.post<ApiResponseModelWrapper<Party>>(this.basePath, body).pipe(
      map(({ success, error, data, reloadData }) => {
        if (success) {
          return data;
        } else {
          let newError = {
            message: error,
            reloadData: null,
          };
          if (reloadData) {
            newError = { ...newError, reloadData };
          }
          throw newError;
        }
      }),
      catchError((err) => {
        if (err?.reloadData === null || err?.reloadData) {
          return throwError(err);
        }
        const error =
          err && err.error && err.error.error
            ? err.error.error
            : 'server error, please try again';
        return throwError({ message: error });
      })
    );
  }

  makeId(): string {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < this.waitlistIdLength; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
  }

  unique(arr: Party[]): Party[] {
    return arr.filter(function ({_id}) {
      return !this.has(_id) && this.add(_id)
    }, new Set());
  }

  getPartyPhone(parsedPhone: string): Observable<string> {
    return this.http
      .get<ApiResponseModelWrapper<string>>(`/v.1/who/party/${parsedPhone}`)
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            return data;
          } else {
            throw new Error(`${error}`);
          }
        }),
        catchError(() => {
          return of(null);
        }),
        //filter null value from catchError
        filter(res => !!res)
      );
  }

  savePartiesBulk(bulkParties, sendConfirmation?: boolean) {
    return this.http
      .post<any>('/v.1/reservations/bulk', {reservations: bulkParties, sendConfirmation})
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            this.updateParty(data);
            return data;
          } else {
            throw new Error(`${error}`);
          }
        })
      );
  }

  getParties(): Observable<{parties: Party[];}> {
    return this.http
      .get<ApiResponseModelWrapper<{ parties: Party[]; counts: number }>>('/v.1/getParties')
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            return data;
          }
          throw new Error(String(error));
        }),
        catchError(() => of({
          parties: [],
          counts: 0,
        })),
      );
  }

  getActiveParties(options?: { includeSeated?: boolean }): Observable<{ parties: Party[]; count: number }> {
    const params: Record<string, string> = {};
    if (options?.includeSeated) {
      params.includeSeated = 'true';
    }
    return this.http
      .get<ApiResponseModelWrapper<{ parties: Party[]; count: number }>>('/v.1/parties', { params })
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            return data;
          }

          throw new Error(String(error));
        }),
        catchError(() => of({
          parties: [],
          count: 0,
        })),
      );
  }

  getWaitlistedParties(locationIds: string[]): Observable<IWaitingPartiesResponse[]> {
    return this.http.post<IWaitingPartiesResponse[]>('/v.1/getParties/waiting', locationIds)
      .pipe(
        catchError(() => of([])),
      )
  }

  getReservations(pageData: IPageData): Observable<{ parties: Party[]; count: number; }> {
    const defaultData: IPageData = {
      page: 1,
      limit: 40,
      type: '',
      search: '',
      sort: {
        quoted_time: 1,
      },
      date: '',
    };
    const requestData: IPageData = Object.assign(defaultData, pageData);

    const {
      page,
      limit,
      type,
      search,
      sort,
      date
    } = requestData;
    /* eslint-enable no-magic-numbers */
    const params: IParams = {
      page,
      limit,
      search,
      sort: JSON.stringify(sort),
      date,
    };

    if (type) {
      params.type = type;
    }
    return this.http
      .get<ApiResponseModelWrapper<any>>('/v.1/getReservations', { params })
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            return data;
          }

          throw new Error(`${error}`);
        }),
        catchError((): any => of({
          parties: [],
          count: 0,
        })),
      );
  }

  // TODO(IParty→Party): retype alongside saveParty().
  updateParty(party: IParty): void {
    this.parties = this.upsert(
      this.parties,
      {
        _id: party._id,
      },
      party
    );
  }

  // TODO(IParty→Party): retype alongside saveParty().
  upsert(arr: IParty[], key: { _id: string }, newVal: IParty): IParty[] {
    const index = arr.findIndex(item => item._id === key._id);
    if (index === -1) {
      arr.push(newVal);
    } else {
      arr.splice(index, 1, newVal);
    }
    return arr;
  }

  findResourcesServicesForNewParty(
    date,
    type,
    services = [],
    resources = [],
    size,
    duration,
    partyId = '',
    ignoreOverlap = false,
    hoursWithDate?: [number, number][],
  ) {
    const config: DataForNewPartyRequest = {
      date,
      type,
      services,
      resources,
      size: size || 0,
      duration,
      partyId,
      ignoreOverlap,
      hoursWithDate,
    };

    return this.http
      .post<ApiResponseModelWrapper<IDataNewPartyApiResponse>>(
        '/v.1/dataForNewParty',
        config,
      )
      .pipe(
        map(({ success, error, data }) => {
          if (success) {
            return data;
          } else {
            throw new Error(`${error}`);
          }
        })
      );
  }

  getPartiesByWorkTime(data: PartiesByWorkTimeRequest) {
    const defaultData = {
      workTime: null,
      type: null,
      served: null,
    };
    Object.assign(defaultData, data);
    return this.http
      .post<Party[]>(
        '/v.1/partiesByWorkTime',
        defaultData
      );
  }

  getById(id: string): Observable<Party> {
    return this.http.get<Party>(`${this.basePath}/${id}`);
  }

  // TODO(IParty→Party): widen param to Party; only reads shared fields.
  showCheckInButton(party: IParty, timeNow: number = Date.now()): boolean {
    const accessCheckInButtonTime = 120 * 60 * 1000; // min * sec * ms
    return !party.checkin_time && party.quoted_time <= timeNow + accessCheckInButtonTime;
  }

  getUpcoming(locationId: string): Observable<number> {
    return this.http.get<number>(`${this.basePath}/${locationId}/upcoming`);
  }

  getUpcomingForAccount(): Observable<number> {
    return this.http.get<number>(`${this.basePath}/upcoming`);
  }

  // TODO: move into separate service, e.g. PartyUtilsService
  calculateCapacityInSelectedResources(resources = [], selectedResources = []) {
    const fullDataOfSelectedResources = resources.filter(resource => selectedResources.includes(resource._id));

    return this.calculateResourcesCapacity(fullDataOfSelectedResources);
  }

  // TODO: move into separate service, e.g. PartyUtilsService
  calculateResourcesCapacity(resources = []) {
    return resources.reduce((acc, resource) => {
      acc.min += resource.capacity.min || 0;
      acc.max += resource.capacity.max || 0;

      return acc;
    }, {
      min: 0,
      max: 0,
    });
  }

  clean<T extends Record<string, any>>(obj: T): Record<string, any> {
    const {overrideIsWorkTime, ...rest} = obj;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rest)) {
      if (key === 'messagingProvider' || !(value === null || value === undefined)) {
        result[key] = value;
      }
    }

    return result;
  }
}
